/**
 * One TikTok player on the page at a time.
 *
 * TikTok blocks pages that load many of its players, so the Audio Studio only ever mounts a single player iframe:
 * trend cards, the right-hand stage and the song drawer all ask for the one slot by key ("card:<trend>", "stage",
 * "drawer"). Taking the slot hands it over; whoever lost it falls back to its poster with a play button.
 * Module-level so no provider is needed.
 */
import { useSyncExternalStore } from 'react';

let active: string | null = null;
const listeners = new Set<() => void>();

function set(key: string | null) {
  if (active === key) return;
  active = key;
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/** Take the slot for `key` (the previous holder goes back to its poster). */
export function claimTikTokPlayer(key: string) { set(key); }

/** Give the slot back, only if `key` still holds it. */
export function releaseTikTokPlayer(key: string) { if (active === key) set(null); }

/** Whether `key` holds the slot. */
export function useTikTokPlayer(key: string): boolean {
  return useSyncExternalStore(subscribe, () => active === key, () => false);
}

/** Tests: nobody holds the slot. */
export function resetTikTokPlayers() { set(null); }
